/**
 * Układ menu bocznego — deklaratywnie.
 *
 * Port z FreightFusion (`apps/app/src/admin/CustomMenu.jsx`, stan z 15.09.2026).
 * Zapis pochodzenia: PORT-FROM-TLSAPP.md
 *
 * **Zmiana wobec źródła: układ jest danymi, nie kodem.** Oryginał ma 510 linii
 * z listą zasobów wpisaną na sztywno i warunkami roli rozsianymi po JSX. Miało
 * to dwie wady, które w tej instancji są dotkliwe:
 *
 * 1. Menu pokazywało pozycje niezależnie od tego, czy zasób jest zarejestrowany.
 *    W repo Marathona moduły dochodzą stopniowo, więc przeniesienie listy
 *    wprost dałoby martwe linki do ekranów, których jeszcze nie ma.
 * 2. Dodanie zasobu dla kolejnego klienta wymagało edycji JSX. Zadanie
 *    „czyszczenie aplikacji do wersji rozbudowywalnej o innych klientów"
 *    mówi wprost, żeby takich miejsc nie zostawiać.
 *
 * Tutaj menu opisuje **co mogłoby być**, a komponent rysuje wyłącznie to, co
 * jest faktycznie zarejestrowane w `<Admin>` i na co pozwala rola.
 *
 * ⛔ Czego nie ma i dlaczego — decyzja z zadania 869f1fuza:
 * mapa, kalkulator dystansu, linie, rodzynki, dashboard floty, limity czatu,
 * monitor prędkości, samodzielne listy ofert przewoźników i ofert giełdowych,
 * ekrany zleceń TMS oraz dokumenty kierowców.
 *
 * ## Sekcje działów (01.10.2026)
 *
 * Pozycje robocze mają wymagane uprawnienie (`permission`) z mapy ról
 * (`permissions.tsx`): Flota, kierowcy i naczepy — planowanie FTL;
 * przewoźnicy i kolejka publikacji — Trade. Wcześniej te cztery ostatnie
 * stały za rolami administracyjnymi (warunek `isBasicUser` ze źródła), więc
 * zwykły użytkownik ich nie widział, choć to sekcje robocze — teraz widzi
 * je każdy z uprawnieniem działu. Pozycje administracji zostają przy rolach.
 *
 * Przyjęcie zleceń (01.10.2026): „Skrzynka” i „Zlecenia VIA” to narzędzia CS
 * i Administracji — widać je tylko z uprawnieniem `ORDER_INTAKE`.
 * „Prezlecenia” i „Zlecenia z maila” zostają w menu dla każdego: FTL i Trade
 * mają do nich podgląd (Administracja §15), przyciski akcji ukrywa ekran.
 *
 * Moduły spółek (T.01, 06.10.2026): pozycja modułu (`module`) znika, gdy
 * moduł jest wyłączony w kontekście konta — w jego spółce, a dla konta bez
 * spółki we wszystkich (reguła `modules.ts`, ta sama co w API).
 */

import { hasPermission, isElementHidden, type PermissionKey } from './permissions';
import { roleAccessFor } from './roleCenter/roleAccessStore';
import type { ModuleKey } from './modules';

/** Dział przyjęcia zleceń — jak `intake/intakeSides.ts` (bez haków Reacta). */
export type MenuIntakeSide = 'cs' | 'administration';

/** Role, jakie zwraca `authProvider.getPermissions()`. */
export const ROLE_ADMIN = 'ADMIN';
export const ROLE_COMPANY_ADMIN = 'COMPANY_ADMIN';
export const ROLE_USER = 'USER';

export type MenuItemConfig = {
  /** Nazwa zasobu zarejestrowanego w `<Admin>`. */
  resource: string;
  /** Etykieta w menu. */
  label: string;
  /**
   * Role, które widzą tę pozycję. Pominięte = widoczna dla wszystkich.
   * Odwzorowuje warunek `["ADMIN","COMPANY_ADMIN"]` ze źródła (administracja).
   */
  role?: string[];
  /**
   * Uprawnienie równoważne liście `role` (centrum ról, 22.10.2026): przy
   * działającym centrum pozycja z listą ról idzie za tym uprawnieniem — rola
   * własna z uprawnieniem administracyjnym też ją widzi. Wartości są
   * dokładnie równoważne mapie w kodzie (sekcja administracyjna, zarządzanie
   * użytkownikami albo spółkami mają te same role co lista), więc z danymi
   * startowymi centrum menu jest takie jak dotąd. Bez centrum — lista ról.
   */
  rolePermission?: PermissionKey;
  /**
   * Uprawnienie wymagane, żeby pozycja była widoczna (sekcje działów,
   * 01.10.2026). Pominięte = bez wymogu. Z `role` — muszą być spełnione oba.
   */
  permission?: PermissionKey;
  /**
   * Moduł spółki, do którego należy ekran (T.01, 06.10.2026). Wyłączony
   * w kontekście konta — pozycji nie ma. Pominięte = ekran poza modułami.
   */
  module?: ModuleKey;
  /**
   * Przełącznik funkcji „do decyzji”, za którym stoi ekran (15.10.2026).
   * Wyłączony dla konta — pozycji nie ma (wersja „obecna” jak produkcja).
   * Pominięte = ekran poza przełącznikami.
   */
  feature?: string;
  /**
   * Dział przyjęcia zleceń, do którego należy ekran (makieta back office v2,
   * fala B, 21.10.2026): z włączonym `intakeDepartments` pozycję widzi tylko
   * ten dział; wyłączony — wystarcza `permission`. Pominięte = bez działu.
   */
  intakeSide?: MenuIntakeSide;
  /**
   * Uprawnienie nadawane imiennie na koncie (fala B, 21.10.2026), które też
   * otwiera pozycję — obok `permission` z roli. Przy pozycji „Wnioski
   * o wyjątek”: Trade z roli albo „Akceptacja wyjątku…” imiennie.
   */
  grant?: string;
};

export type MenuGroup = {
  /** Pusty tytuł = pozycje bez nagłówka, na górze menu. */
  title?: string;
  pozycje: MenuItemConfig[];
};

const MANAGER_ROLES = [ROLE_ADMIN, ROLE_COMPANY_ADMIN];

export const MENU_LAYOUT: MenuGroup[] = [
  {
    pozycje: [
      { resource: 'user-offers', label: 'Zlecenia' },
      // Pulpity FTL (makieta back office v2, A4, 20.10.2026) — za
      // przełącznikami; planista ma swój także na starcie (Team Leader zaczyna
      // od pulpitu ogólnego — decyzja użytkownika 20.10.2026).
      {
        resource: 'ftl-planner-dashboard',
        label: 'Pulpit planisty',
        permission: 'FTL_PLANNING',
        feature: 'plannerDashboard',
      },
      {
        resource: 'ftl-team-leader-dashboard',
        label: 'Pulpit Team Leadera',
        permission: 'FTL_ASSIGN',
        feature: 'teamLeaderDashboard',
      },
      // Pulpit Trade (makieta back office v2, A9, 20.10.2026) — za
      // przełącznikiem `tradeDashboard`, dla działu Trade.
      {
        resource: 'trade-dashboard',
        label: 'Pulpit Trade',
        permission: 'TRADE',
        feature: 'tradeDashboard',
      },
      // Pulpit przyjęcia zleceń (makieta back office v2, fala B, B1,
      // 21.10.2026) — kolejki CS i Administracji, za `intakeDepartments`.
      {
        resource: 'intake-dashboard',
        label: 'Pulpit przyjęcia zleceń',
        permission: 'ORDER_INTAKE',
        feature: 'intakeDepartments',
      },
      { resource: 'emails', label: 'Skrzynka', permission: 'ORDER_INTAKE' },
      { resource: 'pre-orders', label: 'Prezlecenia' },
      // Oś mailowa (mail → prezlecenie → zlecenie → zapis do VIA). Nazwa
      // W UI „Zlecenia", nie „Bank ofert"/„Bank zleceń" (869f1fw98: klient chce
      // odciąć się od nazewnictwa poprzedniego dostawcy). W dokumentach i kodzie
      // „bank zleceń" zostaje pojęciem — to nadal `user-offers`, tam pracuje planista.
      { resource: 'orders', label: 'Zlecenia z maila' },
      { resource: 'via-orders', label: 'Zlecenia VIA', permission: 'ORDER_INTAKE' },
      // „Uwagi od FTL” dla przyjęcia zleceń (makieta back office v2,
      // 20.10.2026) — odbiorca do czasu działu CS: „Przyjęcie zleceń”.
      // Z działami (fala B, 21.10.2026) odbiorcą jest CS.
      {
        resource: 'ftl-notes',
        label: 'Uwagi od FTL',
        permission: 'ORDER_INTAKE',
        feature: 'ftlNotes',
        intakeSide: 'cs',
      },
      // Zmiany zleceń „było → ma być” (makieta back office v2, fala B, B9,
      // 21.10.2026) — lista dla CS i Administracji, za `orderChangeRegister`.
      {
        resource: 'order-changes',
        label: 'Zmiany zleceń',
        permission: 'ORDER_INTAKE',
        feature: 'orderChangeRegister',
      },
      // Kolejka CS (paczka p. Mariusza 21.10, BR-CS-11; fala C, 21.10.2026)
      // — po terminie i krytyczności, z filtrami; za `csLight`.
      {
        resource: 'cs-queue',
        label: 'Kolejka CS',
        permission: 'ORDER_INTAKE',
        feature: 'csLight',
        intakeSide: 'cs',
      },
      // Powiadomienia działów z historią (15.10.2026, Etap 1 makiety FTL).
      { resource: 'notifications', label: 'Powiadomienia', feature: 'notificationsPage' },
    ],
  },
  {
    title: 'Flota',
    pozycje: [
      { resource: 'cars', label: 'Samochody', permission: 'FTL_PLANNING' },
      // Plan pracy floty: obsadzenia i warianty aut na osi dni (29.09.2026).
      {
        resource: 'fleet-schedule',
        label: 'Harmonogram floty',
        permission: 'FTL_PLANNING',
      },
    ],
  },
  // Przewoźnicy (także podwykonawcy) i kontrahenci-klienci to dwie różne
  // kartoteki i nie mieszamy ich w jednej sekcji. Do rozwijanej sekcji „Dane
  // podstawowe” idą klienci z czarną listą oraz kierowcy i naczepy; samochody
  // zostają osobno razem z harmonogramem. Zakres Etapu I v2, uzupełnienie
  // z 14.09 (za transkrypcją zakresu minimalnego, [M] 2:51–7:03, 6:34–8:52,
  // 14:47–15:08).
  // Bez nagłówka grupy (07.10.2026): rozwijana grupa „Przewoźnicy” z jedną
  // pozycją „Przewoźnicy” była dodatkowym kliknięciem bez treści (lista
  // komunikatów z 01.10). Pozycja stoi w menu wprost, w tym samym miejscu.
  {
    pozycje: [
      {
        resource: 'carriers',
        label: 'Przewoźnicy',
        permission: 'TRADE',
        module: 'carriers',
      },
    ],
  },
  {
    title: 'Dane podstawowe',
    pozycje: [
      {
        resource: 'contractors',
        label: 'Kontrahenci',
        role: MANAGER_ROLES,
        rolePermission: 'ADMIN_SECTION',
      },
      { resource: 'drivers', label: 'Kierowcy', permission: 'FTL_PLANNING' },
      { resource: 'trailers', label: 'Naczepy', permission: 'FTL_PLANNING' },
    ],
  },
  {
    title: 'Giełdy',
    pozycje: [
      {
        resource: 'postings',
        label: 'Kolejka publikacji',
        permission: 'TRADE',
        module: 'exchanges',
      },
      // Wnioski o wyjątek od bramki publikacji (makieta back office v2,
      // fala B, 21.10.2026) — dla Trade i osób z uprawnieniem imiennym
      // „Akceptacja wyjątku od zakazu podwykonawcy”; licznik czekających.
      {
        resource: 'publication-exceptions',
        label: 'Wnioski o wyjątek',
        permission: 'TRADE',
        grant: 'PUBLICATION_EXCEPTION_APPROVE',
        module: 'exchanges',
        feature: 'publicationExceptionRequests',
      },
    ],
  },
  {
    title: 'Administracja',
    pozycje: [
      {
        resource: 'users',
        label: 'Użytkownicy',
        role: MANAGER_ROLES,
        rolePermission: 'MANAGE_USERS',
      },
      {
        resource: 'companies',
        label: 'Spółki',
        role: [ROLE_ADMIN],
        rolePermission: 'MANAGE_COMPANIES',
      },
      {
        resource: 'credentials',
        label: 'Poświadczenia',
        role: MANAGER_ROLES,
        rolePermission: 'MANAGE_USERS',
      },
      {
        resource: 'admin-log',
        label: 'Dziennik działań',
        role: MANAGER_ROLES,
        rolePermission: 'MANAGE_USERS',
      },
      // Parametry podpowiadania aut z historią zmian (zadanie 8, 28.09.2026).
      // „Rekomendacji”, nie „podpowiedzi” — tak nazywa je karta zlecenia
      // (poprawa brzmienia 01.10.2026).
      {
        resource: 'suggestion-parameters',
        label: 'Parametry rekomendacji',
        role: MANAGER_ROLES,
        rolePermission: 'ADMIN_SECTION',
      },
      // Przełączniki funkcji „do decyzji” (decyzja użytkownika 12.10.2026).
      {
        resource: 'feature-flags',
        label: 'Przełączniki funkcji',
        role: MANAGER_ROLES,
        rolePermission: 'ADMIN_SECTION',
      },
      // Kto realizuje wg TMS: flota czy Trade (odpowiedzi 16.10.2026, pyt. 4a
      // i 30) — za przełącznikiem `executionDepartmentHint`.
      {
        resource: 'execution-department-mappings',
        label: 'Kto realizuje wg TMS',
        role: MANAGER_ROLES,
        rolePermission: 'ADMIN_SECTION',
        feature: 'executionDepartmentHint',
      },
      // Kalendarz zakazów ruchu ciężarówek (odpowiedź 16.10.2026 pyt. 11) —
      // podgląd tylko do odczytu, za przełącznikiem `truckBanCalendar`.
      {
        resource: 'truck-bans',
        label: 'Kalendarz zakazów',
        role: MANAGER_ROLES,
        rolePermission: 'ADMIN_SECTION',
        feature: 'truckBanCalendar',
      },
      // Powody przekazania do Trade i zwrotu (makieta back office v2, A6,
      // 20.10.2026) — za przełącznikiem `tradeTransferReasons`.
      {
        resource: 'trade-transfer-reasons',
        label: 'Powody przekazania do Trade',
        role: MANAGER_ROLES,
        rolePermission: 'ADMIN_SECTION',
        feature: 'tradeTransferReasons',
      },
      // Korekty paliwowe (makieta back office v2, fala B, B4, 21.10.2026) —
      // kartoteka dla przyjęcia zleceń (Administracja i CS): decyduje
      // uprawnienie, nie rola; za przełącznikiem `fuelSurcharges`.
      {
        resource: 'fuel-surcharges',
        label: 'Korekty paliwowe',
        permission: 'ORDER_INTAKE',
        feature: 'fuelSurcharges',
      },
      // Centrum ról i uprawnień (decyzja użytkownika 22.10.2026, S-22.10-01)
      // — wyłącznie administrator (API: 403 każdemu innemu), za `roleCenter`.
      // Bez równoważnego uprawnienia: wstępu nie da się nadać innej roli.
      {
        resource: 'role-center',
        label: 'Centrum ról i uprawnień',
        role: [ROLE_ADMIN],
        feature: 'roleCenter',
      },
      // Pomiar użycia (decyzja użytkownika 22.10.2026, S-22.10-02) — pomiar
      // pracy pracowników, więc wyłącznie rola ADMIN (także nie administrator
      // spółki); API odmawia 403 każdemu innemu. Za `usageTelemetry`.
      {
        resource: 'usage-analytics',
        label: 'Pomiar użycia',
        role: [ROLE_ADMIN],
        feature: 'usageTelemetry',
      },
    ],
  },
];

/**
 * Etykieta pozycji menu dla zasobu — tytuł ekranu w niebieskim pasku.
 *
 * Tytuł i pozycja menu rozjeżdżały się („Dziennik działań” w menu,
 * „Dziennik administracyjny” na ekranie), a dwa ekrany nie miały tytułu
 * wcale (e2e na dev, 28.09.2026). Ekrany biorą tytuł stąd, więc zmiana
 * nazwy w menu zmienia też tytuł. Zasób spoza menu to błąd programisty —
 * wychodzi w teście ekranu, nie po cichu pustym paskiem.
 */
export function menuLabel(resource: string, layout: MenuGroup[] = MENU_LAYOUT): string {
  for (const group of layout) {
    const item = group.pozycje.find((p) => p.resource === resource);
    if (item) return item.label;
  }
  throw new Error(`Zasobu „${resource}” nie ma w menu`);
}

/**
 * Czy rola widzi daną pozycję.
 * Brak wskazania ról i uprawnienia = pozycja widoczna dla każdego zalogowanego.
 */
export function roleSeesItem(
  item: MenuItemConfig,
  userRole: string | undefined,
  /** Uprawnienia imienne konta (fala B, 21.10.2026); pominięte = brak. */
  grants: readonly string[] = [],
  /** Stan centrum ról innej roli (podgląd „Zobacz jako”); pominięte = zalogowany. */
  access: MenuAccess = sessionAccess(userRole),
): boolean {
  const granted = item.grant !== undefined && grants.includes(item.grant);
  if (item.permission && !access.has(item.permission) && !granted) return false;
  // Centrum ról (22.10.2026): pozycja ukryta dla roli w „Co widzi”. Pozycja
  // otwarta osobie, a nie roli (uprawnienie imienne albo — od 25.10.2026 —
  // CS przy wnioskach o wyjątek), nie zależy od „Co widzi” roli: katalog
  // chowa ją domyślnie roli bez wymaganego uprawnienia (np. CS bez Trade),
  // a opis elementu mówi, że osoba uprawniona widzi ją niezależnie od roli.
  const byGrantOnly = granted && !!item.permission && !access.has(item.permission);
  if (!byGrantOnly && access.hidden(menuElementKey(item.resource))) return false;
  if (!item.role) return true;
  if (!userRole) return false;
  if (item.rolePermission && access.fromCenter) return access.has(item.rolePermission);
  return item.role.includes(userRole);
}

/**
 * Uprawnienia i widoczność, według których liczymy menu (centrum ról,
 * 22.10.2026): zalogowany — lustro albo stan z API; podgląd „Zobacz jako” —
 * dane wybranej roli z centrum.
 */
export type MenuAccess = {
  has: (permission: PermissionKey) => boolean;
  hidden: (element: string) => boolean;
  /** Czy to stan centrum ról (pozycje z `rolePermission` idą za uprawnieniem). */
  fromCenter: boolean;
};

/** Klucz pozycji menu w katalogu elementów centrum ról. */
export const menuElementKey = (resource: string) => `menu.${resource}`;

/** Stan dla zalogowanej roli. */
export function sessionAccess(userRole: string | undefined): MenuAccess {
  return {
    has: (p) => hasPermission(userRole, p),
    hidden: (e) => isElementHidden(userRole, e),
    fromCenter: roleAccessFor(userRole) !== null,
  };
}

/**
 * Zwraca układ ograniczony do zasobów faktycznie zarejestrowanych w `<Admin>`
 * i widocznych dla podanej roli. Grupy bez pozycji znikają w całości — żeby
 * w menu nie zostawał sam nagłówek.
 */
export function visibleGroups(
  layout: MenuGroup[],
  registered: string[],
  userRole: string | undefined,
  /** Czy moduł działa w kontekście konta; pominięte = wszystkie działają. */
  moduleEnabled: (module: ModuleKey) => boolean = () => true,
  /** Czy przełącznik funkcji działa dla konta; pominięte = wszystkie (15.10.2026). */
  featureEnabled: (feature: string) => boolean = () => true,
  /** Czy konto pracuje w dziale przyjęcia zleceń (fala B); pominięte = tak. */
  intakeSideEnabled: (side: MenuIntakeSide) => boolean = () => true,
  /** Uprawnienia imienne konta (fala B, 21.10.2026); pominięte = brak. */
  grants: readonly string[] = [],
  /** Podgląd „Zobacz jako” (centrum ról); pominięte = zalogowany. */
  access: MenuAccess = sessionAccess(userRole),
): MenuGroup[] {
  return layout
    .map((group) => ({
      ...group,
      pozycje: group.pozycje.filter(
        (p) =>
          registered.includes(p.resource) &&
          roleSeesItem(p, userRole, grants, access) &&
          (!p.module || moduleEnabled(p.module)) &&
          (!p.feature || featureEnabled(p.feature)) &&
          (!p.intakeSide || intakeSideEnabled(p.intakeSide)),
      ),
    }))
    .filter((group) => group.pozycje.length > 0);
}

/**
 * Pozycje schowane przez profil wydania (25.10.2026, S-25.10-02; decyzja
 * użytkownika 24.10.2026: „łatwo ukryć z panelu admina”): wyłączona paczka
 * z opcją „ukryj też z menu” chowa swoje dotychczasowe ekrany. Lista
 * przychodzi z API (`hiddenMenu`); tylko menu — trasy API działają dalej.
 * Grupa bez pozycji znika w całości, jak w `visibleGroups`.
 */
export function withoutHiddenResources(groups: MenuGroup[], hidden: readonly string[]): MenuGroup[] {
  if (hidden.length === 0) return groups;
  return groups
    .map((group) => ({ ...group, pozycje: group.pozycje.filter((p) => !hidden.includes(p.resource)) }))
    .filter((group) => group.pozycje.length > 0);
}
